import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Builds Authorization header.
 * @param {string} token
 * @returns {Object}
 */
const authHeader = (token) => ({ Authorization: `Bearer ${token}` });

// ---------------------------------------------------------------------------
// Process a payment
// ---------------------------------------------------------------------------

/**
 * Process a payment.
 * @param {Object} paymentData
 * @param {string} paymentData.method — PAYMENT_METHODS value
 * @param {Object} paymentData.formData — method-specific form data
 * @param {string} paymentData.orderId
 * @param {string} [paymentData.couponCode]
 * @param {string} token
 * @returns {Promise<import('../types/index').PaymentResult>}
 */
export async function processPayment(paymentData, token) {
    const response = await axios.post(
        `${API}/payments/process`,
        paymentData,
        { headers: authHeader(token) }
    );
    return response.data;
}

// ---------------------------------------------------------------------------
// Coupon
// ---------------------------------------------------------------------------

/**
 * Validate and apply a coupon code.
 * @param {string} code
 * @param {number} subtotal
 * @param {string} token
 * @returns {Promise<import('../types/index').CouponResult>}
 */
export async function applyCoupon(code, subtotal, token) {
    const response = await axios.post(
        `${API}/payments/coupon/validate`,
        { code, subtotal },
        { headers: authHeader(token) }
    );
    return response.data;
}

// ---------------------------------------------------------------------------
// Saved Payment Methods
// ---------------------------------------------------------------------------

/**
 * Fetch saved payment methods for the current user.
 * @param {string} token
 * @returns {Promise<import('../types/index').SavedPaymentMethod[]>}
 */
export async function fetchSavedMethods(token) {
    const response = await axios.get(
        `${API}/payments/methods`,
        { headers: authHeader(token) }
    );
    return response.data;
}

/**
 * Save a payment method for future use.
 * @param {Object} methodData
 * @param {string} token
 * @returns {Promise<import('../types/index').SavedPaymentMethod>}
 */
export async function savePaymentMethod(methodData, token) {
    const response = await axios.post(
        `${API}/payments/methods`,
        methodData,
        { headers: authHeader(token) }
    );
    return response.data;
}

/**
 * Delete a saved payment method.
 * @param {string} methodId
 * @param {string} token
 * @returns {Promise<void>}
 */
export async function deleteSavedMethod(methodId, token) {
    await axios.delete(
        `${API}/payments/methods/${methodId}`,
        { headers: authHeader(token) }
    );
}
